<template>
  <AppPanel :padding-content="false">
    <div class="app-upload-document">
      <div class="header">
        <div class="text text--bold">{{ label }}</div>
        <div class="text text--secondary">
          {{ $t("inputs.upload_document.supported_types", { types: acceptString }) }}
          ({{ $t("inputs.upload_document.maximum_size_MB", { size: maximumSize }) }})
        </div>
        <AppButton size="m" :variant="buttonVariant" text="Upload file" @onclick="upload" />
      </div>
      <div v-if="!hideFile || error" class="file">
        <div v-if="!modelValue?.fileName" class="text">{{ $t("inputs.upload_document.no_document_uploaded") }}</div>
        <div v-if="modelValue?.fileName" class="container">
          <a
            v-if="fileNameAndExtension"
            target="_blank"
            @click="downloadFile" 
            class="text text--ellipsis text--link fake-link"
            :title="$t('buttons.download')"
            >{{ modelValue.fileName }}</a
          >
          <span v-if="modelValue.fileName && !fileNameAndExtension" class="text text--ellipsis">{{ modelValue.fileName }}</span>
          <AppIconOnlyButton icon="delete" :title="$t('buttons.delete')" size="xl" @click="deleteDocumentClick" />
        </div>
        <AppInputError v-if="error" :error="error" />
      </div>
      <input ref="fileInput" type="file" :accept="accept" class="not-displayed" @change="fileInputChanged" />
      <div v-if="closeButton" class="close"><AppLinkButton :text="$t('shared.close')" @click="$emit('close')" /></div>
    </div>
  </AppPanel>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import AppButton from "@/components/AppButton.vue";
import AppPanel from "@/components/AppPanel.vue";
import AppLinkButton from "@/components/AppLinkButton.vue";
import AppIconOnlyButton from "@/components/AppIconOnlyButton.vue";
import AppInputError from "@/components/AppInputError.vue";
import { convertMIMEToExtension } from "@/utils/fileUtils";
import type { FileAttachment } from "@/models/fileAttachment";
import { useUploadDocument } from "@/components/uploadDocument/useUploadDocument";
import useEmployeeService from "@/services/useEmployeeService";
import useCompanyGoalsService from "@/services/useCompanyGoalsService";

interface Props {
  accept: string;
  buttonVariant?: "primary" | "secondary";
  closeButton?: boolean;
  downloadUrl?: string;
  error?: string;
  label: string;
  maximumSize: number;
  modelValue?: FileAttachment;
  withoutModal?: boolean;
  hideFile?: boolean;
  employeeId?: number;
  experienceId?: number;
  goalId?: number;
  contentSection?: string;  
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "update:modelValue", document: FileAttachment | undefined): void;
  (e: "close"): void;
}>();

const fileInput = ref<HTMLInputElement>();
const contentSection = ref(props.contentSection || "");
const fileNameAndExtension = ref<string>(props.modelValue?.fileName || "");
const attachmentUrl = ref<string>(fileNameAndExtension.value);

const { upload, clearFileSelector, deleteDocument, getFileFromInputAndClear } = useUploadDocument(fileInput, props);

const resetFileSelector = () => {
  clearFileSelector();
  emit("update:modelValue", undefined);
};
const deleteDocumentClick = () => deleteDocument(resetFileSelector);
const { getJobExperienceAttachment, getEducationAttachment, getEmployeePicture } = useEmployeeService();
const { getGoalPicture } = useCompanyGoalsService();

const fileInputChanged = async () => {
  const file = await getFileFromInputAndClear();
  if (file) {
    emit("update:modelValue", file);
  }
};

const acceptString = computed<string>(() => {
  return props.accept
    .split(",")
    .map((type) => convertMIMEToExtension(type))
    .join(", ");
});

const downloadFile = async () => {
  if (!attachmentUrl.value) {
    return;
  }

  if (props.employeeId || props?.experienceId) {
    let blob: Blob | undefined = undefined;
    try {
      switch (contentSection.value) {
        case 'education':
          if (props.employeeId !== undefined && props.experienceId !== undefined) {
            blob = await getEducationAttachment(props.employeeId, props.experienceId);
          }
          break;
        case 'employee':
          if (props.employeeId !== undefined) {
            blob = await getEmployeePicture(props.employeeId);
          }
          break;
        case 'company-goal':
          if (props.goalId !== undefined) {
            blob = await getGoalPicture(props.goalId);
          }
          break;
        default:
          if (props.employeeId !== undefined && props.experienceId !== undefined) {
            blob = await getJobExperienceAttachment(props.employeeId, props.experienceId);
          }
          break;
      }
      if (blob) {
        attachmentUrl.value = URL.createObjectURL(blob);
      } else {
        attachmentUrl.value = "";
      }
    } catch {
      attachmentUrl.value = "";
    }

    const link = document.createElement("a");
    link.href = attachmentUrl.value;
    link.download = fileNameAndExtension.value;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
</script>

<style scoped lang="scss">
.app-upload-document {
  display: flex;
  flex-direction: column;
}
.header {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
  padding: var(--size-s);
  border-bottom: 1px solid var(--color-border);
  gap: var(--size-xs);
}
.file {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: var(--size-s);
}
.container {
  display: flex;
  justify-content: space-between;
  place-items: center;
}
.close {
  display: flex;
  justify-content: flex-end;
  padding: var(--size-s);
  border-top: 1px solid var(--color-border);
}

.fake-link {
  color: var(--color-link, #1a73e8); // o el que uses para enlaces
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    text-decoration: none; // opcional
  }
}

</style>
